import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logout } from '@/services/auth';
import { APP_PIN_LEN } from '@/constants/appPinLock';
import { useAppPinLock } from '@/contexts/AppPinLockContext';
import { AppPinDots } from '@/components/app-pin/AppPinDots';
import { AppPinKeypad } from '@/components/app-pin/AppPinKeypad';

export function AppPinLockScreen() {
  const { t } = useTranslation();
  const { tryUnlock, lockoutUntilMs } = useAppPinLock();
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const inFlight = useRef(false);

  const lockedOut = lockoutUntilMs > now;

  useEffect(() => {
    if (!lockedOut) return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [lockedOut]);

  const submit = useCallback(
    async (value: string) => {
      if (inFlight.current || lockedOut) return;
      if (value.length !== APP_PIN_LEN) return;
      inFlight.current = true;
      setBusy(true);
      setError(null);
      const result = await tryUnlock(value);
      inFlight.current = false;
      setBusy(false);
      setPin('');
      if (result === 'ok') return;
      if (result === 'locked_out') {
        setError(t('pinLock.lockedOut'));
        return;
      }
      setError(t('pinLock.wrongPin'));
    },
    [lockedOut, t, tryUnlock]
  );

  const onDigit = useCallback(
    (d: string) => {
      if (busy || lockedOut || inFlight.current) return;
      setError(null);
      setPin((prev) => {
        if (prev.length >= APP_PIN_LEN) return prev;
        const next = prev + d;
        if (next.length === APP_PIN_LEN) {
          window.setTimeout(() => {
            void submit(next);
          }, 0);
        }
        return next;
      });
    },
    [busy, lockedOut, submit]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Backspace') {
        e.preventDefault();
        if (busy || lockedOut) return;
        setPin((p) => p.slice(0, -1));
        return;
      }
      if (/^\d$/.test(e.key)) {
        e.preventDefault();
        onDigit(e.key);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, lockedOut, onDigit]);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-background px-6 py-8">
      <div className="flex w-[17.5rem] max-w-full flex-col items-center">
        <img src="/pigeio.png" alt="" className="mb-4 h-12 w-12 rounded-md border border-border object-cover" />
        <h1 className="font-logo text-2xl text-foreground">Duvpost</h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">{t('pinLock.enterHint')}</p>
        <div className="mt-6">
          <AppPinDots length={pin.length} />
        </div>
        {error ? <p className="mt-3 text-center text-sm text-destructive">{error}</p> : <div className="mt-3 h-5" />}
        <div className="mt-4 w-full">
          <AppPinKeypad
            disabled={busy || lockedOut}
            onDigit={onDigit}
            onBackspace={() => {
              if (busy || lockedOut) return;
              setPin((p) => p.slice(0, -1));
            }}
          />
        </div>
        <button
          type="button"
          className="mt-6 text-sm text-muted-foreground hover:text-foreground"
          onClick={() => void logout()}
        >
          {t('nav.logout')}
        </button>
      </div>
    </div>
  );
}
